import { useMutation, useQuery } from "@tanstack/react-query";
import {
  firefliesService,
  firefliesTranscriptQueryKey,
  firefliesTranscriptsQueryKey,
} from "@/services/firefliesService";
import { formatMeetingTranscript } from "@/lib/fireflies/formatMeetingTranscript";
import { deductCreditsForFormatResponse } from "@/lib/fireflies/deductFormatCredits";
import type { FirefliesListParams, FormattedMeetingNotes } from "@/types/fireflies";

const LIST_STALE_MS = 5 * 60_000;

export type FormatFirefliesMeetingResult = {
  notes: FormattedMeetingNotes;
  creditsDeducted: number;
  hadEnoughBalance: boolean;
  creditError?: string | null;
};

export function useFirefliesTranscripts(
  userId: string | undefined,
  params: FirefliesListParams = {}
) {
  return useQuery({
    queryKey: firefliesTranscriptsQueryKey(userId, params),
    queryFn: () => firefliesService.listTranscripts(params),
    enabled: !!userId,
    staleTime: LIST_STALE_MS,
  });
}

export function useFirefliesTranscript(transcriptId: string | undefined) {
  return useQuery({
    queryKey: firefliesTranscriptQueryKey(transcriptId),
    queryFn: () => firefliesService.getTranscript(transcriptId!),
    enabled: !!transcriptId,
    staleTime: Infinity,
  });
}

export function useFormatFirefliesMeeting() {
  return useMutation({
    mutationFn: async (transcriptId: string): Promise<FormatFirefliesMeetingResult> => {
      const detail = await firefliesService.getTranscript(transcriptId);
      const { notes, model, usage } = await formatMeetingTranscript(detail);

      let creditsDeducted = 0;
      let hadEnoughBalance = true;
      let creditError: string | null = null;

      try {
        const creditResult = await deductCreditsForFormatResponse(model, usage);
        if (creditResult) {
          creditsDeducted = creditResult.creditsDeducted;
          hadEnoughBalance = creditResult.hadEnoughBalance;
        }
      } catch (err) {
        console.error("Fireflies format credit deduction error:", err);
        creditError =
          err instanceof Error ? err.message : "Error processing credits";
      }

      return { notes, creditsDeducted, hadEnoughBalance, creditError };
    },
  });
}
